import { useState, useEffect, useRef } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";

const FeatureShowcase = () => {
  const [hoveredFeature, ] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const features = [
    {
      id: "fee-structure",
      title: "Fee Structure Management",
      description:
        "Create program-wise fee heads, terms, and schedules with flexible rules.",
      icon: "CreditCard",
      color: "success",
      stats: { templates: "Unlimited", flexible: "Yes", realtime: "Yes" },
      benefits: [
        "Fee heads & terms",
        "Installments",
        "Late fee rules",
        "Multi-gateway ready",
      ],
    },
    {
      id: "fee-concession",
      title: "Fee Concession Management",
      description:
        "Rule-based concessions for categories, siblings, and scholarships.",
      icon: "Percent",
      color: "primary",
      stats: { scholarship: "Yes", audits: "Yes", yearwise: "Yes" },
      benefits: [
        "Category/sibling/scholarship",
        "Scope by class/program",
        "Auto-apply during billing",
        "Approval logs",
      ],
    },
    {
      id: "fee-special",
      title: "Fee Special Case Management",
      description:
        "Handle one-off waivers, special cases, and negotiated fees. Also handles fee adjustment requests.",
      icon: "Tag",
      color: "warning",
      stats: { cases: "8+", approvals: "Yes", audits: "Yes" },
      benefits: [
        "Case-by-case overrides",
        "Caps & approvals",
        "Audit trail",
        "Temporary pricing",
      ],
    },
    {
      id: "attendance",
      title: "Attendance Management (Student & Employee)",
      description:
        "Subject-wise and daily attendance for students, plus shift-aware employee attendance.",
      icon: "Users",
      color: "accent",
      stats: { modes: "Subject/Daily", mobile: "Yes", coverage: "Stu/Emp" },
      benefits: [
        "Student: subject & daily modes",
        "Employee: shifts & calendars",
        "Notifications & bulk mark",
        "Automated reports",
      ],
    },
    {
      id: "timetable-template",
      title: "Timetable Template Management",
      description: "Build reusable timetable templates with conflict checks.",
      icon: "Calendar",
      color: "primary",
      stats: { templates: "Unlimited", conflicts: "Auto", optimize: "Yes" },
      benefits: [
        "Drag-and-drop builder",
        "Teacher load view",
        "Room allocation",
        "Conflict detection",
      ],
    },
    {
      id: "exam-scheduling",
      title: "Exam Scheduling Management",
      description: "Plan exam slots, invigilation, and venues without clashes.",
      icon: "CalendarCheck",
      color: "success",
      stats: { slots: "Unlimited", clashes: "0", invigilators: "Auto" },
      benefits: [
        "Auto slotting",
        "Invigilator assignment",
        "Room/seat planning",
        "Exam notifications",
      ],
    },
    {
      id: "marks-performance",
      title: "Marks and Performance Management",
      description:
        "Record marks, compute grades, and track performance trends.",
      icon: "BarChart3",
      color: "accent",
      stats: { schemes: "Custom", analytics: "Yes", reports: "Progress" },
      benefits: [
        "Custom grade schemes",
        "Weighted averages",
        "Result publishing",
        "Progress cards",
      ],
    },
    {
      id: "exam-management",
      title: "Exam Scheduling Management",
      description: "End-to-end exam lifecycle from creation to results.",
      icon: "FileText",
      color: "secondary",
      stats: { types: "Unlimited", efficiency: "90%", creation: "Custom" },
      benefits: [
        "Question banks & blueprints",
        "Objective/subjective papers",
        "Moderation",
        "Result workflows",
      ],
    },
    {
      id: "leave-student",
      title: "Student Leave Management",
      description:
        "Easy student leave requests with approvals and attendance sync.",
      icon: "Moon",
      color: "warning",
      stats: { types: "Unlimited", approval: "Yes", sync: "Attendance" },
      benefits: [
        "Apply via portal",
        "Parent approval",
        "Auto attendance update",
        "Reason tracking",
      ],
    },
    {
      id: "leave-employee",
      title: "Employee Leave Management",
      description:
        "Configurable leave policies, carry-forward, and encashment.",
      icon: "CalendarPlus",
      color: "primary",
      stats: { policies: "Unlimited", limit: "Custom", transparency: "100%" },
      benefits: [
        "Policy engine",
        "Carry forward",
        "Encashment rules",
        "Calendar integration",
      ],
    },
    {
      id: "homework",
      title: "Homework and Assignment Management",
      description: "Create, distribute, and track homework and assignments.",
      icon: "ClipboardCheck",
      color: "secondary",
      stats: {
        templates: "Unlimited",
        submissions: "Digital",
        audits: "Yes",
      },
      benefits: [
        "Assignment templates",
        "Rubrics",
        "Submission tracking",
        "Parent alerts",
      ],
    },
    {
      id: "position",
      title: "Multiple Position Management",
      description: "Define positions, departments, and reporting lines.",
      icon: "UserCog",
      color: "accent",
      stats: { roles: "Unlimited", hierarchy: "Yes", transfers: "Yes" },
      benefits: [
        "Org structure",
        "Reporting lines",
        "Role mapping",
        "Transfers",
      ],
    },
    {
      id: "roles-policy",
      title: "Roles and Policy Management",
      description: "Granular roles, permissions, and policy enforcement.",
      icon: "ShieldCheck",
      color: "warning",
      stats: { rbac: "Yes", granularity: "Field-level", audits: "Yes" },
      benefits: [
        "RBAC",
        "Field-level controls",
        "Audit logs",
        "Access reviews",
      ],
    },
    {
      id: "subjects",
      title: "Subjects Mapping Management",
      description: "Create subjects, map to classes, and tie to curriculum.",
      icon: "BookOpen",
      color: "primary",
      stats: { subjects: "Unlimited", mapping: "Class", optional: "Yes" },
      benefits: [
        "Subject catalog",
        "Class mapping",
        "Optional subjects",
        "Code mapping",
      ],
    },
    {
      id: "curriculum",
      title: "Curriculum Design Management",
      description: "Design curriculum, outcomes, and term-wise breakdowns.",
      icon: "Layers",
      color: "success",
      stats: {
        versions: "Multi",
        alignment: "Custom",
        planners: "Custom",
      },
      benefits: [
        "Outcomes & competencies",
        "Term planners",
        "Syllabus maps",
        "Versioning",
      ],
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef?.current) {
      observer?.observe(sectionRef?.current);
    }

    return () => observer?.disconnect();
  }, []);

  return (
    <section
      id="features"
      ref={sectionRef}
      className="relative z-10 py-8 sm:py-16 lg:py-20 bg-gradient-to-b from-surface to-background scroll-mt-24"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div
          className={`text-center mb-16 transform transition-all duration-1000 translate-y-0 opacity-100 ${
            isVisible
              ? "md:translate-y-0 md:opacity-100"
              : "md:translate-y-8 md:opacity-0"
          }`}
        >
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-inter font-600 mb-4 sm:mb-6">
            <Icon name="Grid3X3" size={16} color="currentColor" />
            <span>16 Comprehensive Modules</span>
          </div>

          <h2 className="font-jakarta font-normal text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-foreground mb-4 sm:mb-6">
            Every Feature Your Institution{" "}
            <span className="bg-gradient-to-r from-primary font-normal to-secondary bg-clip-text text-transparent">
              Actually Needs
            </span>
          </h2>

          <p className="font-inter font-500 text-base sm:text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            From admissions to analytics, KnowTran covers every aspect of
            educational administration with intelligent automation and real-time
            insights.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5 lg:gap-6 mb-12 sm:mb-16">
          {features?.map((feature, index) => (
            <div
              key={feature?.id}
              className={`group relative p-4 sm:p-5 lg:p-6 bg-white rounded-2xl border-2 border-white/20 hover:border-${
                feature?.color
              }/30 transition-all duration-500 cursor-pointer aurora-glass hover:shadow-2xl transform hover:-translate-y-2 translate-y-0 opacity-100 ${
                isVisible
                  ? "md:translate-y-0 md:opacity-100"
                  : "md:translate-y-8 md:opacity-0"
              }`}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Feature Icon */}
              <div
                className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-${feature?.color}/10 flex items-center justify-center mb-3 sm:mb-4 group-hover:bg-${feature?.color}/20 transition-all duration-300`}
              >
                <Icon
                  name={feature?.icon}
                  size={24}
                  color={`var(--color-${feature?.color})`}
                  strokeWidth={2}
                />
              </div>

              {/* Feature Content */}
              <div className="space-y-2.5 sm:space-y-3">
                <h3 className="font-jakarta font-700 text-base sm:text-lg text-foreground group-hover:text-primary transition-colors duration-300">
                  {feature?.title}
                </h3>

                <p className="font-inter font-500 text-sm text-muted-foreground line-clamp-3">
                  {feature?.description}
                </p>

                {/* Stats Preview */}
                <div className="grid grid-cols-3 gap-2 pt-2.5 sm:pt-3 border-t border-white/20">
                  {Object.entries(feature?.stats)?.map(
                    ([key, value], _) => (
                      <div key={key} className="text-center">
                        <div
                          className={`font-jakarta font-700 text-xs sm:text-sm text-${feature?.color}`}
                        >
                          {value}
                        </div>
                        <div className="font-inter font-400 text-[10px] sm:text-xs text-muted-foreground capitalize">
                          {key}
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>

              {/* Hover Overlay */}
              <div
                className={`absolute inset-0 bg-${feature?.color}/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none`}
              />

              {/* Expand Icon */}
              <div className="absolute top-3 sm:top-4 right-3 sm:right-4 opacity-0 group-hover:opacity-100 transition-all duration-300">
                <div
                  className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-${feature?.color}/10 flex items-center justify-center`}
                >
                  <Icon
                    name="ArrowUpRight"
                    size={14}
                    color={`var(--color-${feature?.color})`}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Feature Detail Modal/Expanded View */}
        {hoveredFeature && (
          <div
            className={`transform transition-all duration-1000 translate-y-0 opacity-100 ${
              isVisible
                ? "md:translate-y-0 md:opacity-100"
                : "md:translate-y-8 md:opacity-0"
            }`}
          >
            {(() => {
              const feature = features?.find((f) => f?.id === hoveredFeature);
              return (
                <div className="bg-white rounded-3xl p-6 sm:p-8 border-2 border-primary/20 shadow-2xl aurora-glow">
                  <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 items-center">
                    {/* Left Content */}
                    <div className="space-y-4 sm:space-y-6">
                      <div className="flex items-center space-x-3 sm:space-x-4">
                        <div
                          className={`w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-${feature?.color}/10 flex items-center justify-center`}
                        >
                          <Icon
                            name={feature?.icon || ""}
                            size={28}
                            color={`var(--color-${feature?.color})`}
                            strokeWidth={2}
                          />
                        </div>
                        <div>
                          <h3 className="font-jakarta font-800 text-xl sm:text-2xl text-foreground">
                            {feature?.title}
                          </h3>
                          <p className="font-inter font-500 text-sm sm:text-base text-muted-foreground">
                            Advanced {feature?.title?.toLowerCase()} solution
                          </p>
                        </div>
                      </div>

                      <p className="font-inter font-500 text-base sm:text-lg text-muted-foreground">
                        {feature?.description}
                      </p>

                      {/* Benefits List */}
                      <div className="space-y-2.5 sm:space-y-3">
                        <h4 className="font-jakarta font-700 text-base sm:text-lg text-foreground">
                          Key Benefits:
                        </h4>
                        <div className="grid md:grid-cols-2 gap-2.5 sm:gap-3">
                          {feature?.benefits?.map((benefit, index) => (
                            <div
                              key={index}
                              className="flex items-center space-x-2.5 sm:space-x-3"
                            >
                              <Icon
                                name="Check"
                                size={16}
                                color={`var(--color-${feature?.color})`}
                                strokeWidth={3}
                              />
                              <span className="font-inter font-500 text-sm sm:text-base text-foreground">
                                {benefit}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <Button
                        variant="default"
                        size="lg"
                        onClick={() =>
                          document
                            .getElementById("demo-booking")
                            ?.scrollIntoView({ behavior: "smooth" })
                        }
                        className="font-jakarta font-600 aurora-glow"
                        iconName="Play"
                        iconPosition="left"
                        iconSize={18}
                      >
                        See {feature?.title} in Action
                      </Button>
                    </div>

                    {/* Right Stats */}
                    <div className="space-y-6">
                      <h4 className="font-jakarta font-700 text-lg sm:text-xl text-foreground">
                        Performance Metrics
                      </h4>

                      <div className="grid gap-3 sm:gap-4">
                        {Object.entries(feature?.stats || {})?.map(
                          ([key, value], _) => (
                            <div
                              key={key}
                              className={`p-4 sm:p-5 lg:p-6 rounded-2xl bg-${feature?.color}/5 border border-${feature?.color}/20`}
                            >
                              <div className="flex items-center justify-between mb-1.5 sm:mb-2">
                                <span className="font-inter font-500 text-xs sm:text-sm text-muted-foreground capitalize">
                                  {key?.replace(/([A-Z])/g, " $1")?.trim()}
                                </span>
                                <Icon
                                  name="TrendingUp"
                                  size={16}
                                  color={`var(--color-${feature?.color})`}
                                />
                              </div>
                              <div
                                className={`font-jakarta font-800 text-2xl sm:text-3xl text-${feature?.color}`}
                              >
                                {value}
                              </div>
                            </div>
                          )
                        )}
                      </div>

                      {/* Mini Chart Visualization */}
                      <div className="p-4 sm:p-6 bg-muted/30 rounded-2xl">
                        <h5 className="font-jakarta font-600 text-sm sm:text-base text-foreground mb-3 sm:mb-4">
                          Usage Trends
                        </h5>
                        <div className="h-20 sm:h-24 flex items-end justify-around space-x-1">
                          {[65, 78, 52, 89, 67, 94, 73, 85]?.map(
                            (height, index) => (
                              <div
                                key={index}
                                className={`bg-gradient-to-t from-${
                                  feature?.color
                                } to-${
                                  feature?.color
                                }/50 rounded-t-sm flex-1 transition-all duration-1000 delay-${
                                  index * 100
                                }`}
                                style={{ height: `${height}%` }}
                              />
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Bottom CTA */}
        <div
          className={`text-center mt-14 sm:mt-16 transform transition-all duration-1000 translate-y-0 opacity-100 ${
            isVisible
              ? "md:translate-y-0 md:opacity-100"
              : "md:translate-y-8 md:opacity-0"
          }`}
        >
          <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-2xl p-6 sm:p-8 border border-primary/20">
            <h3 className="font-jakarta font-700 text-xl sm:text-2xl text-foreground mb-3 sm:mb-4">
              Ready to See All Features in Action?
            </h3>
            <p className="font-inter font-500 text-sm sm:text-base text-muted-foreground mb-5 sm:mb-6 max-w-2xl mx-auto">
              Experience the complete KnowTran platform with a personalized demo
              tailored to your institution's needs
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <Button
                variant="default"
                size="lg"
                onClick={() =>
                  document
                    .getElementById("demo-booking")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                className="font-jakarta font-600 text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 aurora-glow"
                iconName="Calendar"
                iconPosition="left"
                iconSize={20}
              >
                Book Complete Demo
              </Button>
              {/* <Button
                variant="outline"
                size="lg"
                onClick={() =>
                  document
                    .getElementById("product-tour")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                className="font-jakarta font-600"
                iconName="MousePointer"
                iconPosition="left"
                iconSize={20}
              >
                Interactive Tour
              </Button> */}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeatureShowcase;
